'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Save, FolderOpen, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import type { CalendarPreset } from '@/lib/calendar-presets-db';
import type { PaperSize } from '@/lib/pdf/pdf-constants';

type PDFFormat = 'list' | 'annual' | 'monthly-calendar' | 'monthly-list' | 'saddle-stitch';

const FORMAT_LABELS: Record<PDFFormat, string> = {
  list: 'List View',
  annual: 'Annual Grid',
  'monthly-calendar': 'Monthly Wall',
  'monthly-list': 'Monthly List',
  'saddle-stitch': 'Folding Calendar',
};

interface CalendarPresetManagerProps {
  presets: CalendarPreset[];
  activePresetId: string | null;
  currentState: {
    selectedFormat: PDFFormat;
    paperSize: PaperSize;
    selectedYear: number;
    selectedImages: Record<string, string>;
  };
  onLoad: (preset: CalendarPreset) => void;
  onSave: (preset: CalendarPreset) => Promise<CalendarPreset>;
  onDelete: (id: string) => Promise<void>;
}

export function CalendarPresetManager({
  presets,
  activePresetId,
  currentState,
  onLoad,
  onSave,
  onDelete,
}: CalendarPresetManagerProps) {
  const [saveOpen, setSaveOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const { toast } = useToast();

  const activePreset = activePresetId
    ? presets.find((p) => p.id === activePresetId)
    : null;

  const defaultName = `${currentState.selectedYear} ${FORMAT_LABELS[currentState.selectedFormat]}`;

  const handleSave = async (saveAsNew: boolean) => {
    const name = presetName.trim() || defaultName;
    const now = Date.now();

    const preset: CalendarPreset = {
      id: !saveAsNew && activePreset ? activePreset.id : crypto.randomUUID(),
      name,
      selectedFormat: currentState.selectedFormat,
      paperSize: currentState.paperSize,
      selectedYear: currentState.selectedYear,
      selectedImages: currentState.selectedImages,
      createdAt: !saveAsNew && activePreset ? activePreset.createdAt : now,
      updatedAt: now,
    };

    try {
      await onSave(preset);
      setSaveOpen(false);
      setPresetName('');
      toast({ title: saveAsNew ? 'Preset saved' : 'Preset updated', description: name });
    } catch {
      toast({ title: 'Failed to save preset', variant: 'destructive' });
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string, name: string) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await onDelete(id);
      toast({ title: 'Preset deleted', description: name });
    } catch {
      toast({ title: 'Failed to delete preset', variant: 'destructive' });
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Load */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={presets.length === 0}>
            <FolderOpen className="w-4 h-4 mr-1" />
            Load
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel>Saved Presets</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {presets.map((p) => (
            <DropdownMenuItem key={p.id} onClick={() => onLoad(p)} className="flex justify-between items-center">
              <div className="flex flex-col min-w-0 flex-1">
                <span className="truncate font-medium">{p.name}</span>
                <span className="text-xs text-muted-foreground">
                  {p.selectedYear} &middot; {p.paperSize.toUpperCase()}
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 ml-2 shrink-0"
                onClick={(e) => handleDelete(e, p.id, p.name)}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Save */}
      <Popover open={saveOpen} onOpenChange={setSaveOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            <Save className="w-4 h-4 mr-1" />
            Save
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72">
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Preset Name</label>
              <Input
                placeholder={defaultName}
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSave(!activePreset);
                }}
              />
            </div>
            <div className="flex gap-2">
              {activePreset ? (
                <>
                  <Button size="sm" className="flex-1" onClick={() => handleSave(false)}>
                    Save
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => handleSave(true)}>
                    Save As...
                  </Button>
                </>
              ) : (
                <Button size="sm" className="w-full" onClick={() => handleSave(true)}>
                  Save
                </Button>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
